import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Fingerprint, Loader2, ShieldCheck, LogOut, FileText, Newspaper, FileDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full border border-navy-border bg-navy-deep/70 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-gold";
const STATUSES = ["Docket Filed", "Under Review", "Hearing Scheduled", "Award Issued"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

export const PortalModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [records, setRecords] = useState({ intakes: [], press: [] });

  useEffect(() => {
    if (!open) return;
    setChecking(true);
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [open]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/intake`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/press`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([i, p]) => setRecords({ intakes: i.data || [], press: p.data || [] }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: fd.get("email"), password: fd.get("password") },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(t.portal.errorInvalid);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setRecords({ intakes: [], press: [] });
  };

  const changeStatus = async (r, val) => {
    try {
      await axios.patch(`${API}/intake/${r.id}/status`, { status: val }, { withCredentials: true });
      setRecords((rec) => ({
        ...rec,
        intakes: rec.intakes.map((x) => (x.id === r.id ? { ...x, status: val } : x)),
      }));
      toast.success(t.portal.updated);
    } catch {
      toast.error(t.portal.errorInvalid);
    }
  };

  const statusIdx = (s) => Math.max(0, STATUSES.indexOf(s));

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="portal-login-modal" className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-navy-deep/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`relative max-h-[92vh] w-full overflow-y-auto border border-gold/30 bg-navy-card shadow-2xl ${user ? "max-w-3xl" : "max-w-md"}`}>
            <div className="flex items-start justify-between border-b border-navy-border px-7 py-6">
              <div>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <Fingerprint className="h-3.5 w-3.5" /> {user ? t.portal.welcome : t.portal.tag}
                </div>
                <h3 className="mt-2 font-serif text-2xl font-medium text-slate-50">
                  {user ? user.name : t.portal.title}
                </h3>
                {user && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">{user.title || user.role} · {user.email}</p>}
              </div>
              <button onClick={onClose} data-testid="portal-close-button" aria-label="Close"
                className="p-1.5 text-slate-400 transition-colors hover:text-gold">
                <X className="h-5 w-5" />
              </button>
            </div>

            {checking ? (
              <div className="flex items-center justify-center gap-3 px-7 py-16 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin text-gold" />
                <span className="font-mono text-xs uppercase tracking-[0.2em]">{t.portal.verifying}</span>
              </div>
            ) : !user ? (
              <form onSubmit={submit} data-testid="portal-login-form" className="space-y-5 px-7 py-7">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.portal.email}</label>
                  <input required type="email" name="email" data-testid="portal-email-input" placeholder="registrar@lexaegis-international.com" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.portal.password}</label>
                  <input required type="password" name="password" data-testid="portal-password-input" placeholder="••••••••••" className={inputCls} />
                </div>
                {error && (
                  <p data-testid="portal-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
                )}
                <button type="submit" disabled={loading} data-testid="portal-submit-button"
                  className="flex w-full items-center justify-center gap-3 border border-gold bg-gold/10 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? t.portal.verifying : t.portal.submit}
                </button>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-slate-500">
                  {t.portal.footnote}
                </p>
              </form>
            ) : (
              <div data-testid="portal-dashboard" className="grid gap-6 px-7 py-7 sm:grid-cols-2">
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                    <FileText className="h-4 w-4" /> {t.portal.dockets}
                    <span className="text-slate-500">({records.intakes.length})</span>
                  </h4>
                  <div className="max-h-72 space-y-2.5 overflow-y-auto pr-1" data-testid="portal-dockets-list">
                    {records.intakes.length === 0 && <p className="border border-navy-border bg-navy-deep/50 px-4 py-5 text-sm text-slate-500">{t.portal.empty}</p>}
                    {records.intakes.map((r) => (
                      <div key={r.id} data-testid={`docket-row-${r.reference}`}
                        className="border border-navy-border bg-navy-deep/50 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-mono text-sm font-semibold text-gold">{r.reference}</p>
                            <p className="truncate text-xs text-slate-300">{r.claimant_name} v. {r.respondent_name}</p>
                          </div>
                          <span className="shrink-0 font-mono text-[10px] text-slate-500">{fmtDate(r.created_at)}</span>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">{t.portal.statusLabel}</label>
                          <select
                            value={STATUSES.includes(r.status) ? r.status : "Docket Filed"}
                            onChange={(e) => changeStatus(r, e.target.value)}
                            data-testid={`docket-status-${r.reference}`}
                            className="flex-1 appearance-none border border-navy-border bg-navy-deep px-2 py-1.5 text-[11px] text-slate-200 outline-none transition-colors focus:border-gold">
                            {STATUSES.map((s, i) => (
                              <option key={s} value={s} className="bg-navy-deep">{t.portal.statusLabels[i]}</option>
                            ))}
                          </select>
                          <a href={`${API}/intake/${r.id}/pdf`} title={t.portal.downloadPdf}
                            data-testid={`docket-pdf-${r.reference}`}
                            className="flex items-center gap-1.5 border border-gold/40 px-2.5 py-1.5 text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep">
                            <FileDown className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                    <Newspaper className="h-4 w-4" /> {t.portal.inquiries}
                    <span className="text-slate-500">({records.press.length})</span>
                  </h4>
                  <div className="max-h-72 space-y-2.5 overflow-y-auto pr-1" data-testid="portal-press-list">
                    {records.press.length === 0 && <p className="border border-navy-border bg-navy-deep/50 px-4 py-5 text-sm text-slate-500">{t.portal.empty}</p>}
                    {records.press.map((r) => (
                      <div key={r.id} className="border border-navy-border bg-navy-deep/50 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-mono text-sm font-semibold text-gold">{r.reference}</p>
                            <p className="truncate text-xs text-slate-300">{r.full_name} — {r.organization}</p>
                            <p className="truncate font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{r.inquiry_type}</p>
                          </div>
                          <span className="shrink-0 font-mono text-[10px] text-slate-500">{fmtDate(r.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-navy-border pt-5 sm:col-span-2">
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    <ShieldCheck className="h-4 w-4 text-gold" /> ISO 27001 · GDPR Art. 32
                  </span>
                  <button onClick={logout} data-testid="portal-logout-button"
                    className="inline-flex items-center gap-2 border border-gold/40 px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep">
                    <LogOut className="h-3.5 w-3.5" /> {t.portal.logout}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
